import { useEffect } from 'react';
import { reportAgentPermissionError } from '@leagueplatform/core';
import { useGetImpersonationType } from '@leagueplatform/auth-utils';

interface ApiError {
  message: string;
  stack: {
    errors: {
      code: string;
    }[];
  };
  info?: {
    code: string;
  };
}

export interface ImpersonationError extends ApiError {
  message: string;
  stack: {
    errors: {
      code: 'forbidden';
    }[];
  };
  info?: {
    code: 'unauthorized';
  };
}

export function isImpersonationError(
  error: any | any[],
): error is ImpersonationError {
  if (error === null) return false;
  if (Array.isArray(error)) {
    return error.some((err) => isImpersonationError(err));
  }

  if (error?.message?.includes('403')) return true;
  // Rest error
  if (error?.stack?.errors) {
    return error.stack.errors.some((err: any) => err?.code === 'forbidden');
  }
  if (error?.errors) {
    return error.errors.some((err: any) => err?.code === 'forbidden');
  }

  // WS Error Check
  if (typeof error?.message === 'string') {
    try {
      const parsedError = JSON.parse(error.message);
      if (parsedError?.info?.code === 'unauthorized') {
        return true;
      }
    } catch (e) {
      if (error.message.includes('unauthorized')) {
        return true;
      }
    }
  }
  return false;
}

export interface SharedErrorStateProps<TError = unknown> {
  error: TError | TError[];
}

export const useDisplayIsImpersonatorError = ({
  error,
}: SharedErrorStateProps) => {
  const isImpersonationSessionType = useGetImpersonationType();

  const showImpersonationSessionTypeError =
    isImpersonationSessionType && isImpersonationError(error);

  useEffect(() => {
    if (showImpersonationSessionTypeError) {
      reportAgentPermissionError(
        undefined,
        undefined,
        isImpersonationSessionType,
      );
    }
  }, [showImpersonationSessionTypeError, isImpersonationSessionType]);

  return { isImpersonationSessionType, showImpersonationSessionTypeError };
};
